import React from "react";
import { Sparkles, ArrowRight, Phone, ExternalLink, Bookmark, Users } from "lucide-react";
import { Button } from "../ui/button";
import { useJourneyActions, usePlanExtras } from "./useJourneyActions";
import { LocalPicks } from "./LocalPicks";

function CompanionsList({ companions, cityDisplay }) {
  if (companions.length === 0) return null;
  return (
    <div className="mt-6 border-t border-[#C8A96E]/25 pt-5" data-testid="wellness-companions">
      <div className="flex items-center gap-2 mb-3">
        <Users size={14} className="text-[#C8A96E]" />
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#C8A96E]">Travelers heading to {cityDisplay}</span>
      </div>
      <div className="space-y-2">
        {companions.slice(0, 4).map((c) => {
          const color = c.compat_color || "#9E8BB0";
          return (
            <div key={c.user_id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3" data-testid={`wellness-companion-${c.user_id}`}>
              <div>
                <div className="text-sm text-white">{c.user_name}</div>
                <div className="text-[10px] text-[#A89C98]">{c.personalized_tag || `${c.zodiac} · ${c.chinese_zodiac}`} · {c.purpose}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full" style={{ color, border: `1px solid ${color}66`, background: `${color}14` }}>
                  {c.compat_label}
                </span>
                <span className="font-mono-scanner text-sm" style={{ color: c.compat_color || "#C8A96E" }}>{c.compatibility}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LunamoreCTA({ cta, onForward }) {
  return (
    <div className="mt-6 border-t border-[#C8A96E]/25 pt-6">
      <p className="text-sm text-white/80 mb-4">
        Would you like <span className="text-[#C8A96E]">Lunamore</span> to customize and arrange this journey?
      </p>
      <div className="flex flex-wrap gap-3">
        <a data-testid="wellness-book-website" href={cta.website} target="_blank" rel="noopener noreferrer" onMouseDown={onForward} onClick={onForward} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-10 px-5 uppercase tracking-widest text-xs font-bold flex items-center gap-2">
          Book with Lunamore <ExternalLink size={13} />
        </a>
        <a data-testid="wellness-book-phone" href={`tel:${cta.phone_tel}`} onMouseDown={onForward} onClick={onForward} className="border border-[#C8A96E] text-[#C8A96E] hover:bg-[#C8A96E] hover:text-[#15111A] rounded-none h-10 px-5 uppercase tracking-widest text-xs font-bold flex items-center gap-2 transition-colors">
          <Phone size={13} /> {cta.phone}
        </a>
      </div>
    </div>
  );
}

export function PlanResult({ plan, onReset }) {
  const { heroUrl, companions } = usePlanExtras(plan);
  const { savedJourneyId, saving, saveJourney, forwardToLunamore } = useJourneyActions(plan);
  return (
    <div data-testid="wellness-plan-result">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-[#C8A96E]" />
        <span className="text-[10px] uppercase tracking-[0.32em] text-[#C8A96E]">Your Astro Wellness Travel Blueprint</span>
      </div>

      {heroUrl && (
        <div className="mt-3 mb-4 relative overflow-hidden rounded-xl border border-[#C8A96E]/40" data-testid="wellness-hero">
          <img src={heroUrl} alt={plan.destination.city_display} className="w-full h-40 sm:h-56 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#15111A] via-transparent to-transparent" />
        </div>
      )}

      <h2 className="font-heading text-4xl text-white leading-tight" data-testid="wellness-destination-city">{plan.destination.city_display}</h2>
      <p className="text-sm text-[#A89C98] mb-4">{plan.destination.country}</p>
      <p className="text-white/90 leading-relaxed" data-testid="wellness-why">{plan.why}</p>

      <h3 className="mt-6 text-xs uppercase tracking-[0.28em] text-[#C8A96E]">Your 5-Day Journey</h3>
      <ul className="mt-3 space-y-2" data-testid="wellness-itinerary">
        {plan.itinerary.map((line) => (
          <li key={line} className="flex gap-2 text-sm text-white/85 leading-relaxed">
            <ArrowRight size={14} className="text-[#C8A96E] shrink-0 mt-1" />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 rounded-xl border border-[#C8A96E]/50 bg-[#C8A96E]/10 p-4 flex items-baseline gap-3" data-testid="wellness-budget">
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#A89C98]">Estimated Budget</span>
        <span className="font-mono-scanner text-2xl text-[#C8A96E]" data-testid="wellness-budget-display">{plan.budget.display}</span>
      </div>

      <div className="mt-5">
        <Button data-testid="wellness-save-btn" onClick={saveJourney} disabled={saving || !!savedJourneyId} className="border border-[#C8A96E]/50 bg-transparent text-[#C8A96E] hover:bg-[#C8A96E]/10 rounded-none h-10 px-5 uppercase tracking-widest text-xs font-bold flex items-center gap-2">
          <Bookmark size={13} />
          {savedJourneyId ? "Journey Saved" : (saving ? "Saving…" : "Save Journey")}
        </Button>
      </div>

      <CompanionsList companions={companions} cityDisplay={plan.destination.city_display} />
      <LocalPicks picks={plan.local_picks} cityDisplay={plan.destination.city_display} />
      <LunamoreCTA cta={plan.cta} onForward={forwardToLunamore} />

      <button data-testid="wellness-replan-btn" onClick={onReset} className="mt-6 text-xs text-[#A89C98] hover:text-white">← Plan another journey</button>
    </div>
  );
}
